import React from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';

import { FILTER_LOGIC } from '../constants';
import { useStyles } from './styles';
import Chips from '../Chips';
import Checkbox from '../Checkbox';
import useUpdateFilterValue from '../../../hooks/useUpdateFilterValue';
import PartCountLabel from '../PartCountLabel/PartCountLabel';

const SelectionGroup = ({
	multiple,
	hiddenlabel,
	appliedLogic,
	appliedValue,
	cid,
	conditional,
	disabled,
	label,
	logic,
	values,
	categoryId
}) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const logicButtons = [logic, FILTER_LOGIC.NOT];
	const { updateFilterValue, updateFilterLogic } = useUpdateFilterValue();
	const countOfPartsForFilterOptions = useSelector(state => state.filters.countOfPartsForFilterOptions || {});

	const handleLogicChange = newLogic => {
		updateFilterLogic(cid, newLogic);
	};

	const handleCheck = event => {
		updateFilterValue(cid, categoryId, event.target.value);
	};

	const handleCheckMultiple = cbValue => () => {
		const currentIndex = appliedValue.indexOf(cbValue);
		const newValue = [...appliedValue];

		if (currentIndex === -1) {
			newValue.push(cbValue);
		} else {
			newValue.splice(currentIndex, 1);
		}

		updateFilterValue(cid, categoryId, newValue);
	};

	return (
		<Grid container direction="column">
			{multiple && (
				<Grid item className={classes.chipsContainer}>
					<Chips
						fullWidth
						hiddenlabel
						appliedValue={appliedLogic}
						updateAppliedValue={handleLogicChange}
						disabled={disabled}
						label={label}
						values={logicButtons}
						size="small"
					/>
				</Grid>
			)}

			<Grid item className={classes.formControlContainer}>
				<FormControl component="fieldset">
					{hiddenlabel || (
						<FormLabel component="legend" className={classes.formLabel}>
							<Typography variant="body2">{parse(label)}</Typography>
						</FormLabel>
					)}

					{multiple ? (
						<FormGroup>
							{values.map((value, index) => {
								const cbValue = conditional ? value.label : value;
								const isSelected = multiple ? appliedValue.indexOf(cbValue) !== -1 : appliedValue === cbValue;
								const partCountExist = countOfPartsForFilterOptions[cid] && countOfPartsForFilterOptions[cid][cbValue];
								const partCount = partCountExist ? countOfPartsForFilterOptions[cid][cbValue].count : 0;
								const disableByPartcount = partCountExist && partCount === 0;

								return (
									<Checkbox
										color="primary"
										key={cbValue + '-' + index + '-checkbox'}
										gutterVertical
										size="small"
										name={cbValue}
										label={
											<>
												{i18next.exists(`filterValue.${cbValue}`) ? t(`filterValue.${cbValue}`) : cbValue}
												{partCountExist ? <PartCountLabel count={partCount} /> : null}
											</>
										}
										appliedValue={appliedValue.indexOf(cbValue) !== -1}
										updateAppliedValue={handleCheckMultiple(cbValue)}
										disabled={!isSelected && (disabled || disableByPartcount)}
									/>
								);
							})}
						</FormGroup>
					) : (
						<RadioGroup aria-label={cid} name={cid} value={appliedValue} onChange={handleCheck}>
							{values.map((value, index) => {
								const cbValue = conditional ? value.label : value;
								const partCountExist =
									countOfPartsForFilterOptions[cid] && countOfPartsForFilterOptions[cid][value.label];
								const isSelected = multiple ? appliedValue.indexOf(cbValue) !== -1 : appliedValue === cbValue;
								const partCount = partCountExist ? countOfPartsForFilterOptions[cid][value.label].count : 0;
								const disableByPartcount = partCountExist && partCount === 0;
								return (
									<FormControlLabel
										key={cbValue + '-' + index + '-fcl-radio'}
										value={cbValue}
										control={
											<Radio
												color="primary"
												size="small"
												disabled={!isSelected && (disabled || disableByPartcount)}
												classes={{
													root: classes.radioRoot
												}}
											/>
										}
										label={
											<>
												{i18next.exists(`filterValue.${cbValue}`) ? t(`filterValue.${cbValue}`) : cbValue}
												{partCountExist ? <PartCountLabel count={partCount} /> : null}
											</>
										}
									/>
								);
							})}
						</RadioGroup>
					)}
				</FormControl>
			</Grid>
		</Grid>
	);
};

SelectionGroup.propTypes = {
	multiple: PropTypes.bool,
	hiddenlabel: PropTypes.bool,
	appliedLogic: PropTypes.string.isRequired,
	appliedValue: PropTypes.arrayOf(PropTypes.string).isRequired,
	cid: PropTypes.string.isRequired,
	conditional: PropTypes.bool,
	disabled: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
	logic: PropTypes.bool,
	values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired
};

export default SelectionGroup;
